import { subtractBaselineForwardFilled } from '../../../../../helpers/Chart/subtractBaselineForwardFilled';

/** `{ [timestamp]: value }`, the shape the legacy chart consumes for every series. */
export type History = Record<string, number> | undefined;

/** The chart's three series. */
export type ChartModeHistories = {
  stable: History;
  asset: History;
  nap: History;
};

const toPoints = (history: History) =>
  Object.entries(history ?? {})
    .map(([timestamp, value]) => ({ timestamp: Number(timestamp), value }))
    .sort((a, b) => a.timestamp - b.timestamp);

const toHistory = (points: { timestamp: number; value: number }[]): History =>
  points.reduce<Record<string, number>>((acc, point) => {
    acc[point.timestamp] = point.value;
    return acc;
  }, {});

/**
 * Replace the three series with the single outperformance line: stable balance MINUS the
 * no-algorithmic-progression line, the dollars the algorithm added over simply holding.
 *
 * It is stable-only, and that is a property of the data rather than a simplification. The
 * hold baseline holds a CONSTANT asset quantity, so an asset-denominated hold line is flat
 * and subtracting it merely shifts the asset series — asset outperformance and the asset
 * balance would be the same shape twice.
 *
 * There is no profit-only counterpart, and there was one briefly. Subtracting a constant cost
 * basis from every series produces a chart identical to the full balance: the y-domain is
 * stretched to each series' own min/max, so a constant shift preserves the shape exactly and
 * changes nothing but the axis labels.
 *
 * `off` returns the input untouched, so the normal chart pays nothing for this existing.
 */
export const applyOutperformance = (
  histories: ChartModeHistories,
  on: boolean,
): ChartModeHistories => {
  if (!on) {
    return histories;
  }

  // Joined on TIMESTAMP: the balance series carries a point per transaction AND per candle
  // while the hold line is candle-only, so they are different lengths — 316 against 256 on
  // sub 18 — and an index join drifts by the difference. Every hold timestamp does exist in
  // the balance series, so the fill never has to invent one.
  const difference = subtractBaselineForwardFilled(
    toPoints(histories.stable),
    toPoints(histories.nap),
  );

  // EMPTY objects, never undefined. The chart reads every history with `Object.entries`
  // whether or not its series is selected, and `Object.entries(undefined)` throws — which
  // took the whole page down the first time this dropped the two series it does not draw.
  return { stable: toHistory(difference), asset: {}, nap: {} };
};
